import "./Megawin.css";

import { megawinApi } from "api/megawin";
import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";

import CheckoutModal from "./CheckoutModal";
import CustomerModal from "./CustomerModal";
import MegawinLogo from "./MegawinLogo";
import PrizeArtwork from "./PrizeArtwork";
import useMegawinPurchase from "./useMegawinPurchase";

const numberFormat = new Intl.NumberFormat("mn-MN");

const Megawin = () => {
  const [sections, setSections] = useState([]);
  const [selected, setSelected] = useState(null);
  const [ticketCount, setTicketCount] = useState(1);
  const [loading, setLoading] = useState(true);
  const purchase = useMegawinPurchase(selected, ticketCount);
  const {
    phone,
    setPhone,
    customer,
    loadCustomer,
    payment,
    setPayment,
    error,
    setError,
    requestBusy,
    busy,
    submitting,
    checking,
    customerLoading,
    buyTickets,
    checkPayment,
  } = purchase;
  const [customerOpen, setCustomerOpen] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [historyFilter, setHistoryFilter] = useState("Бүгд");

  useEffect(() => {
    let mounted = true;
    megawinApi
      .getSections()
      .then((response) => {
        if (!mounted) return;
        if (response.code !== 1)
          throw new Error(response.msg || "Сугалааг ачаалж чадсангүй.");
        setSections(response.data || []);
        if (response.data?.length) setSelected(response.data[0]);
      })
      .catch(
        (requestError) =>
          mounted &&
          setError(requestError.message || "Сервертэй холбогдож чадсангүй.")
      )
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, []);

  // Counts are paid tickets, not bundles.
  const minTickets = 1;

  const total = (selected?.amount || 0) * ticketCount;

  const chooseSection = (section) => {
    if (requestBusy.current) return;
    if (selected?.id !== section.id) {
      setTicketCount(1);
      setPayment(null);
    }
    setSelected(section);
    setDetailOpen(true);
    setError("");
  };

  const openCheckout = () => {
    if (!busy && selected) setCheckoutOpen(true);
  };
  const renderCard = (section) => (
    <article className="mw-game-card" key={section.id}>
      <button
        className="mw-card-image"
        type="button"
        disabled={busy}
        onClick={() => chooseSection(section)}
        aria-label={`${section.section_name || "Megawin"} дэлгэрэнгүй`}
      >
        <PrizeArtwork section={section} />
      </button>
      <div className="mw-card-content">
        <h3>{section.section_name || "Megawin сугалаа"}</h3>
        <p className="mw-card-availability">
          Үлдсэн: {numberFormat.format(section.ticket_balance || 0)} тасалбар
        </p>
        <p className="mw-price-label">Тасалбарын үнэ</p>
        <strong className="mw-card-price">
          {numberFormat.format(section.amount || 0)}₮
        </strong>
        <button
          type="button"
          className="mw-gold-button"
          disabled={busy || section.ticket_balance < 1}
          onClick={() => {
            chooseSection(section);
            setCheckoutOpen(true);
          }}
        >
          Тасалбар авах <span aria-hidden="true">›</span>
        </button>
      </div>
    </article>
  );

  return (
    <main className="megawin-page">
      <Helmet>
        <title>Megawin | Тасалбар авах</title>
      </Helmet>
      {!detailOpen ? (
        <>
          <section className="mw-hero">
            <div className="mw-hero-content">
              <MegawinLogo />
              <button
                type="button"
                className="mw-gold-button mw-resume"
                onClick={() => setCustomerOpen(true)}
              >
                Тасалбар шалгах <span aria-hidden="true">›</span>
              </button>
              <h1>{selected?.section_name || "MegaWin"}</h1>
              <PrizeArtwork section={selected} hero className="mw-hero-prize" />
              {selected && (
                <button
                  type="button"
                  disabled={busy || selected.ticket_balance < 1}
                  className="mw-gold-button mw-hero-price"
                  aria-label="Онцлох сугалааны тасалбар авах"
                  onClick={openCheckout}
                >
                  {numberFormat.format(selected.amount || 0)}₮{" "}
                  <span aria-hidden="true">›</span>
                </button>
              )}
            </div>
            <svg
              className="mw-hero-wave"
              viewBox="0 0 1440 150"
              preserveAspectRatio="none"
              aria-hidden="true"
            >
              <path
                d="M0 24C250 0 380 150 720 150S1190 0 1440 24V150H0Z"
                fill="#f5f7fa"
              />
            </svg>
          </section>
          <section className="mw-games mw-container">
            <h2>Сугалаанууд</h2>
            {loading && (
              <p className="mw-empty" role="status">
                Сугалаануудыг ачаалж байна…
              </p>
            )}
            {!loading && !sections.length && (
              <p className="mw-empty">
                Одоогоор идэвхтэй сугалаа байхгүй байна.
              </p>
            )}
            <div className="mw-card-grid">{sections.map(renderCard)}</div>
          </section>
          <section className="mw-upcoming mw-container">
            <h2>Удахгүй болох тохирлууд</h2>
            <div className="mw-card-grid">
              {sections
                .filter(
                  (section) =>
                    section.draw_date &&
                    Date.parse(section.draw_date) > Date.now()
                )
                .sort(
                  (a, b) => Date.parse(a.draw_date) - Date.parse(b.draw_date)
                )
                .slice(0, 3)
                .map(renderCard)}
            </div>
            {!sections.some(
              (section) =>
                section.draw_date && Date.parse(section.draw_date) > Date.now()
            ) && <p className="mw-empty">Тохирлын тов удахгүй зарлагдана.</p>}
          </section>
          <section className="mw-history mw-container">
            <h2>Тохирлын түүхүүд</h2>
            <div className="mw-history-tabs" aria-label="Тохирлын төрөл">
              {["Авто машин", "Гар утас", "x1000", "Giveaway", "Бүгд"].map(
                (filter) => (
                  <button
                    key={filter}
                    type="button"
                    aria-pressed={historyFilter === filter}
                    onClick={() => setHistoryFilter(filter)}
                  >
                    {filter}
                  </button>
                )
              )}
            </div>
            <div className="mw-history-table">
              <table>
                <thead>
                  <tr>
                    <th>Тохирол</th>
                    <th>Азтан</th>
                    <th>Азын дугаар</th>
                    <th>Огноо</th>
                    <th>Хонжвор</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td colSpan="5">Тохирлын түүх одоогоор байхгүй байна.</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>
        </>
      ) : (
        selected && (
          <>
            <section className="mw-detail mw-container">
              <button
                type="button"
                className="mw-back"
                disabled={busy}
                onClick={() => setDetailOpen(false)}
              >
                ‹ Сугалаанууд
              </button>
              <div className="mw-detail-heading">
                <h1>{selected.section_name}</h1>
                <h2>Хонжвор</h2>
              </div>
              <div className="mw-detail-layout">
                <div className="mw-prizes">
                  {(selected.lotteries?.length
                    ? selected.lotteries
                    : [
                        {
                          id: selected.id,
                          lottery_name: selected.section_name,
                          image: selected.image,
                        },
                      ]
                  ).map((lottery, index) => (
                    <article key={lottery.id} className="mw-prize-card">
                      <span className="mw-prize-rank" aria-hidden="true">
                        {String(index + 1).padStart(2, "0")}
                      </span>
                      {(lottery.image || selected.image) && (
                        <img src={lottery.image || selected.image} alt="" />
                      )}
                      <h3>{lottery.lottery_name}</h3>
                      {lottery.description && <p>{lottery.description}</p>}
                    </article>
                  ))}
                </div>
                <aside className="mw-purchase">
                  <p>
                    Үлдсэн тасалбар:{" "}
                    <strong>
                      {numberFormat.format(selected.ticket_balance || 0)}
                    </strong>
                  </p>
                  <div className="mw-purchase-controls">
                    <strong>{numberFormat.format(total)}₮</strong>
                    <div className="mw-inline-quantity">
                      <button
                        type="button"
                        aria-label="Тасалбар хасах"
                        disabled={busy || ticketCount <= 1}
                        onClick={() => setTicketCount(ticketCount - 1)}
                      >
                        −
                      </button>
                      <input
                        aria-label="Тасалбарын тоо"
                        type="number"
                        min={minTickets}
                        max={selected.ticket_balance}
                        value={ticketCount}
                        disabled={busy}
                        onChange={(event) =>
                          setTicketCount(Number(event.target.value))
                        }
                      />
                      <button
                        type="button"
                        aria-label="Тасалбар нэмэх"
                        disabled={
                          busy || ticketCount >= selected.ticket_balance
                        }
                        onClick={() => setTicketCount(ticketCount + 1)}
                      >
                        +
                      </button>
                    </div>
                    <button
                      type="button"
                      className="mw-gold-button"
                      disabled={
                        busy ||
                        !Number.isInteger(ticketCount) ||
                        ticketCount < 1 ||
                        ticketCount > selected.ticket_balance
                      }
                      onClick={openCheckout}
                    >
                      Авах <span aria-hidden="true">›</span>
                    </button>
                  </div>
                </aside>
              </div>
              <section className="mw-information">
                <h2>Мэдээлэл</h2>
                <p>{selected.description}</p>
              </section>
            </section>
            <section className="mw-featured-prize">
              <svg
                viewBox="0 0 1440 120"
                preserveAspectRatio="none"
                aria-hidden="true"
              >
                <path
                  d="M0 100C180 -20 280 170 550 65S820 -30 1050 55S1300 110 1440 25V120H0Z"
                  fill="#06369f"
                />
              </svg>
              <h2>{selected.section_name}</h2>
              {selected.image && (
                <img
                  src={selected.image}
                  alt={selected.section_name || "Megawin"}
                />
              )}
            </section>
          </>
        )
      )}
      {detailOpen && (
        <div className="mw-container">
          <button
            type="button"
            className="mw-gold-button"
            onClick={() => setCustomerOpen(true)}
          >
            Тасалбар шалгах
          </button>
        </div>
      )}
      {error && !checkoutOpen && !customerOpen && (
        <p role="alert" className="mw-page-error mw-container">
          {error}
        </p>
      )}
      <CheckoutModal
        open={checkoutOpen}
        onClose={() => setCheckoutOpen(false)}
        selected={selected}
        ticketCount={ticketCount}
        setTicketCount={setTicketCount}
        phone={phone}
        setPhone={setPhone}
        customer={customer}
        loadCustomer={loadCustomer}
        customerLoading={customerLoading}
        sections={sections}
        onCheckTickets={(id) => {
          setCheckoutOpen(false);
          setCustomerOpen(true);
          purchase.checkTickets(id);
        }}
        payment={payment}
        busy={busy}
        submitting={submitting}
        checking={checking}
        error={error}
        buyTickets={buyTickets}
        checkPayment={checkPayment}
      />
      <CustomerModal
        open={customerOpen}
        onClose={() => setCustomerOpen(false)}
        purchase={purchase}
        sections={sections}
      />
    </main>
  );
};

export default Megawin;
